import axiosInstance from "@/lib/axios";
import { viewPreferencesPath } from "@/config/endpoints";
import { getApiBaseUrl } from "@/config/apiBase";

/**
 * DB-backed per-user "Show/hide fields" view preferences. The DB is the
 * source of truth; redux-persist localStorage is only a fast first-paint
 * cache. Keys mirror the redux slice names that own each layout:
 *   - "leadCardFunctionality"    -> main Leads page column layout
 *   - "dynamicCardFunctionality" -> per dynamic-tab column layouts (route map)
 *
 * The same blobs now also carry the List view's header arrangement
 * (`listColumnOrder`) and header-menu filters/sort (`listColumnFilters`,
 * `listColumnSort`), so everything a user does to the list is one record.
 */
export type ViewPreferencesMap = Record<string, unknown>;

export interface ViewPreferencesResponse {
  /** This user's own saved layouts (personal overrides). */
  preferences: ViewPreferencesMap;
  /**
   * The WORKSPACE layouts — what a tenant admin last saved. Everyone in the
   * tenant boots on these; a user's own row is merged on top, so a personal
   * arrangement still wins for that person. Empty on a server that predates
   * the tenant-defaults migration.
   */
  tenantDefaults: ViewPreferencesMap;
  /**
   * "Globalise configs" (Settings → Manage): true = the workspace layouts
   * are imposed over every user's own row and non-admins get the layout
   * controls read-only. Absent on an older server = false.
   */
  globalised: boolean;
}

/**
 * The workspace layouts from the last boot fetch. Kept module-level so
 * "Reset layouts" can put a user back on the WORKSPACE arrangement rather
 * than the factory one — resetting to something nobody configured is what
 * makes a shared layout feel like it "didn't stick".
 */
let cachedTenantDefaults: ViewPreferencesMap = {};
let cachedGlobalised = false;

export function getTenantDefault(viewKey: string): unknown {
  return cachedTenantDefaults[viewKey];
}

/** The "Globalise configs" flag from the last boot fetch. */
export function getViewPrefsGlobalised(): boolean {
  return cachedGlobalised;
}

/** Fetch this user's layouts and the workspace defaults. */
export async function fetchViewPreferences(): Promise<ViewPreferencesResponse> {
  const res = await axiosInstance.get(viewPreferencesPath);
  const tenantDefaults = (res.data?.tenantDefaults ?? {}) as ViewPreferencesMap;
  cachedTenantDefaults = tenantDefaults;
  const globalised = res.data?.globalised === true;
  cachedGlobalised = globalised;
  return {
    preferences: (res.data?.preferences ?? {}) as ViewPreferencesMap,
    tenantDefaults,
    globalised,
  };
}

/** Upsert one view's layout for the current user. */
export async function saveViewPreference(
  viewKey: string,
  config: unknown
): Promise<void> {
  await axiosInstance.put(`${viewPreferencesPath}/${viewKey}`, { config });
}

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

/**
 * Save with retries — a flaky connection must not silently drop a layout
 * change (the local cache would then be purged on the next logout and the
 * user's arrangement would be gone). Three attempts, 1s / 3s apart; throws
 * after the last so the caller can keep it pending for the unload flush.
 */
export async function saveViewPreferenceWithRetry(
  viewKey: string,
  config: unknown,
  delaysMs: number[] = [1000, 3000],
): Promise<void> {
  let lastErr: unknown;
  for (let attempt = 0; attempt <= delaysMs.length; attempt += 1) {
    try {
      await saveViewPreference(viewKey, config);
      return;
    } catch (err) {
      lastErr = err;
      if (attempt < delaysMs.length) await sleep(delaysMs[attempt]);
    }
  }
  throw lastErr;
}

/**
 * Fire-and-forget save that survives the page being closed or navigated
 * away mid-debounce. `fetch` with `keepalive` is the only request the
 * browser lets outlive the document (axios/XHR is cancelled). Same Bearer
 * token the axios instance uses. Returns false when there is no session.
 */
export function saveViewPreferenceKeepalive(
  viewKey: string,
  config: unknown,
): boolean {
  const token = localStorage.getItem("xale_access_token");
  if (!token || typeof fetch !== "function") return false;
  try {
    void fetch(`${getApiBaseUrl()}${viewPreferencesPath}/${viewKey}`, {
      method: "PUT",
      keepalive: true,
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ config }),
    });
    return true;
  } catch {
    return false;
  }
}
